import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Copy, KeyRound, Trash2, Link2 } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeading from "@/components/PageHeading";
import { Switch } from "@/components/ui/switch";

export default function Impostazioni() {
  const { user } = useAuth();
  return (
    <div data-testid="impostazioni-page">
      <PageHeading kicker="08 · ACCOUNT" title="Impostazioni" subtitle="Il tuo account personale, le notifiche e la gestione degli utenti." />
      <AccountSection />
      {user?.is_owner && <UsersSection />}
    </div>
  );
}

function AccountSection() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [prefs, setPrefs] = useState({
    notif_email: user?.notif_email ?? true,
    notif_sms: user?.notif_sms ?? false,
    notif_calendar: user?.notif_calendar ?? true,
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const saveProfile = async () => {
    try {
      await api.patch("/me", f);
      toast.success("Profilo aggiornato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const savePref = async (key, value) => {
    setPrefs({ ...prefs, [key]: value });
    try {
      await api.patch("/me", { [key]: value });
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const changePw = async () => {
    try {
      await api.post("/me/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyIcs = () => {
    navigator.clipboard.writeText(`${API}/ics/user/${user.ics_token}.ics`);
    toast.success("Link ICS copiato. Abbonati da Apple Calendario: le task si aggiornano da sole con avviso 1 ora prima.");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16" data-testid="account-section">
      <div>
        <p className="meta-label mb-5">PROFILO</p>
        <div className="space-y-5 max-w-sm">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome" data-testid="profile-name-input" className="field-line" />
          <input value={user?.email || ""} disabled className="field-line opacity-60" data-testid="profile-email" />
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Numero di telefono" data-testid="profile-phone-input" className="field-line" />
          <button onClick={saveProfile} data-testid="save-profile-button" className="bg-black text-greige px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors">Salva profilo</button>
        </div>

        <p className="meta-label mb-5 mt-12">CAMBIO PASSWORD</p>
        <div className="space-y-5 max-w-sm">
          <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Password attuale" data-testid="current-password-input" className="field-line" />
          <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Nuova password (min 8 caratteri)" data-testid="new-password-input" className="field-line" />
          <button onClick={changePw} data-testid="change-password-button" className="border border-black px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">Aggiorna password</button>
        </div>
      </div>

      <div>
        <p className="meta-label mb-5">PREFERENZE DI NOTIFICA</p>
        <div className="space-y-5 max-w-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-body text-sm text-ink">Email</p>
              <p className="font-body text-xs text-stone2e">Sempre disponibile</p>
            </div>
            <Switch checked={prefs.notif_email} onCheckedChange={(v) => savePref("notif_email", v)} data-testid="notif-email-switch" />
          </div>
          <div className="flex items-center justify-between opacity-60">
            <div>
              <p className="font-body text-sm text-ink">SMS <span className="ml-2 border border-black/30 px-2 py-0.5 text-[0.58rem] uppercase tracking-widest">In arrivo</span></p>
              <p className="font-body text-xs text-stone2e">Disponibile in una fase successiva</p>
            </div>
            <Switch checked={false} disabled data-testid="notif-sms-switch" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-body text-sm text-ink">Notifica via calendario</p>
              <p className="font-body text-xs text-stone2e">Avviso automatico 1 ora prima di ogni scadenza task</p>
            </div>
            <Switch checked={prefs.notif_calendar} onCheckedChange={(v) => savePref("notif_calendar", v)} data-testid="notif-calendar-switch" />
          </div>
          <button onClick={copyIcs} data-testid="copy-my-ics-button" className="inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
            <Link2 size={13} strokeWidth={1.5} /> Copia il mio feed ICS
          </button>
          <p className="font-body text-xs text-stone2e">Aggiungi il feed una sola volta da Apple Calendario (File → Nuovo abbonamento calendario): le task compaiono e si aggiornano automaticamente.</p>
        </div>
      </div>
    </div>
  );
}

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", role: "cliente", brand_id: "" });
  const [inviteLink, setInviteLink] = useState("");

  const load = useCallback(() => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const bmap = Object.fromEntries(brands.map((b) => [b.id, b.name]));

  const create = async () => {
    if (!nf.name.trim() || !nf.email.trim()) {
      toast.error("Nome ed email obbligatori");
      return;
    }
    try {
      const { data } = await api.post("/users", { ...nf, brand_id: nf.brand_id || null });
      setInviteLink(data.invite_link);
      setNf({ name: "", email: "", role: "cliente", brand_id: "" });
      setAdding(false);
      load();
      toast.success("Account creato. Copia il link e invialo tu.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const resetLink = async (u) => {
    const { data } = await api.post(`/users/${u.id}/reset-link`);
    navigator.clipboard.writeText(data.reset_link);
    toast.success("Link di reimpostazione copiato: incollalo e invialo tu.");
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'account di ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="users-section">
      <p className="meta-label mb-5">GESTIONE UTENTI · SOLO PROPRIETARIA</p>

      {inviteLink && (
        <div className="bg-black text-greige p-5 mb-6 max-w-2xl" data-testid="invite-link-box">
          <p className="font-mono2e text-[0.62rem] tracking-widest mb-2">LINK DI INVITO — COPIA E INVIA MANUALMENTE</p>
          <div className="flex items-center gap-3">
            <p className="font-mono2e text-xs break-all flex-1">{inviteLink}</p>
            <button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Copiato"); }} data-testid="copy-invite-button" className="shrink-0 border border-greige/50 p-2 hover:bg-greige hover:text-ink transition-colors"><Copy size={14} strokeWidth={1.5} /></button>
          </div>
        </div>
      )}

      <div className="mb-8">
        {adding ? (
          <div className="bg-white/70 border border-white p-5 max-w-md">
            <input autoFocus value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Nome e cognome *" data-testid="new-user-name" className="field-line mb-3" />
            <input type="email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email *" data-testid="new-user-email" className="field-line mb-3" />
            <select value={nf.role} onChange={(e) => setNf({ ...nf, role: e.target.value })} data-testid="new-user-role" className="field-line bg-transparent mb-3">
              <option value="cliente">Cliente in visione</option>
              <option value="lavoratore">Lavoratore</option>
            </select>
            {nf.role === "cliente" && (
              <select value={nf.brand_id} onChange={(e) => setNf({ ...nf, brand_id: e.target.value })} data-testid="new-user-brand" className="field-line bg-transparent mb-4">
                <option value="">Seleziona brand *</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
            <div className="flex gap-3">
              <button onClick={create} data-testid="confirm-user-button" className="bg-black text-greige px-6 py-2 text-xs uppercase tracking-widest">Crea account</button>
              <button onClick={() => setAdding(false)} className="border border-black px-6 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">Annulla</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} data-testid="create-user-button" className="inline-flex items-center gap-2 bg-black text-greige px-6 py-3 uppercase tracking-widest text-xs hover:bg-stone2e transition-colors">
            <Plus strokeWidth={1.5} size={16} /> Nuovo account
          </button>
        )}
      </div>

      <div className="space-y-px bg-black/10 border border-black/10 max-w-3xl">
        {users.map((u) => (
          <div key={u.id} className="bg-greige/60 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`user-row-${u.id}`}>
            <div className="min-w-0">
              <p className="font-body text-sm text-ink font-medium">
                {u.name} {u.is_owner && <span className="ml-1 border border-black/30 px-1.5 py-0.5 text-[0.55rem] uppercase tracking-widest">Proprietaria</span>}
              </p>
              <p className="meta-label mt-0.5">
                {u.email.toUpperCase()} · {u.role === "lavoratore" ? "LAVORATORE" : `CLIENTE · ${(bmap[u.brand_id] || "—").toUpperCase()}`}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {!u.is_owner && (
                <>
                  <div className="flex items-center gap-2">
                    <Switch checked={u.is_active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} />
                    <span className="font-mono2e text-[0.6rem] text-stone2e">{u.is_active ? "ATTIVO" : "DISATTIVATO"}</span>
                  </div>
                  <button onClick={() => resetLink(u)} data-testid={`reset-link-${u.id}`} title="Genera link password" className="text-stone2e hover:text-ink"><KeyRound size={15} strokeWidth={1.5} /></button>
                  <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="text-stone2e hover:text-ink"><Trash2 size={15} strokeWidth={1.5} /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
